import { useEffect } from 'react';
import { useUploadFiles } from './useUploadFile';
import {
  OneDriveFileUploadInput,
  useOneDriveFileUpload,
} from 'src/data-capture/hooks/file-uploads/useOneDriveFileUpload';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { SelectedFile } from '../types';

type UseCombinedFileUploadArgs = {
  clientId?: string;
  familyId?: string;
  onUploaded: (files: SelectedFile[]) => void;
  onLoadingChange?: (isLoading: boolean) => void;
};

export function useCombinedFileUpload({
  clientId,
  familyId,
  onUploaded,
  onLoadingChange,
}: UseCombinedFileUploadArgs) {
  const { isUploading: isUploadingLocal, handleFileUpload } = useUploadFiles({
    clientId,
    familyId,
    onUploaded,
  });

  const { uploadOneDriveFiles, isUploading: isUploadingOneDrive } = useOneDriveFileUpload({
    clientId,
  });

  const isUploading = isUploadingLocal || isUploadingOneDrive;

  useEffect(() => {
    onLoadingChange?.(isUploading);
  }, [isUploading, onLoadingChange]);

  const handleOneDriveUpload = async (input: OneDriveFileUploadInput) => {
    try {
      const files = await uploadOneDriveFiles(input);
      const selectedFiles = files.map(file => ({
        id: file.id,
        fileId: file.file.id,
        name: file.file.fileName ?? 'Uploading...',
      }));
      onUploaded(selectedFiles);
    } catch (e) {
      notifyAndLogError(
        'Failed to upload files from OneDrive',
        'An error occurred while uploading files from OneDrive. Please try again.',
        e,
      );
    }
  };

  return {
    isUploading,
    handleFileUpload,
    handleOneDriveUpload,
  };
}
